// Added by Sejune Oh on 2019-06-25
// Copyright (c) Coreline Soft, All rights are reserved.
//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
import { Component, createRef } from 'react'
import { observable, action, makeObservable } from 'mobx'
import { inject, observer } from 'mobx-react'

//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
import { AssignedItemProps, ReactFormEvent } from '@/helper/define'
import { getIdList, getQueryPath } from '@/helper/util'
import Intl from '@/helper/Intl'
import Logger from '@/helper/Logger'

//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
import UserAssignedGroup from './UserAssignedGroup'
import { GroupStore, GroupElementProps } from '@/stores/mgmt/group'
import { UserBaseCProps } from '../../../UserBase'
import { MultiSelectItemProps } from '@/components/common/MultiSelect'
import { UserStore } from '@/stores/mgmt/user'
import { MgmtError } from '@/components/common/Template'

//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
interface UserAssignedGroupCProps extends UserBaseCProps {
  user?: UserStore
  group?: GroupStore
  showNotFoundDialog: (err: MgmtError, removeRequired?: boolean) => void
}

//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
@inject('user', 'group')
@observer
class UserAssignedGroupContainer extends Component<UserAssignedGroupCProps, {}> {
  linkedGroup: AssignedItemProps<GroupElementProps> = { assigned: [], unassigned: [] }
  mounted: boolean = false

  _linkedGroupRef = createRef<any>()
  _formSubmitBtnRef = createRef<HTMLButtonElement>()

  constructor(props: UserAssignedGroupCProps) {
    super(props)
    makeObservable(this, {
      linkedGroup: observable,
      mounted: observable,
      componentDidMount: action,
      _setGroupInfo: action,
    })
  }

  componentDidMount = async () => {
    try {
      await this._setGroupInfo(this.props.userId)
      this.mounted = true
      this.props.onChildComponentMount('userAssigned')
    } catch (err) {
      Logger.errLog(err.response ? err.response.data.message : err.message)
    }
  }

  render() {
    return (
      this.mounted && (
        <UserAssignedGroup
          linkedGroupRef={this._linkedGroupRef}
          linkedGroup={this.linkedGroup}
          formSubmitBtnRef={this._formSubmitBtnRef}
          onFormSubmit={this._handleFormSubmit}
        />
      )
    )
  }

  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  _setGroupInfo = async (userId?: string) => {
    const getGroupQuery = getQueryPath({})
    const groupList = await this.props.group!.getElements(getGroupQuery)

    if (!groupList || groupList.length === 0) {
      // prettier-ignore
      const err = Intl.getIntlText({ id: 'htmljs_react.mgmt.userassignedgroupcontainer.dialog_desc_group_required' })
      this.props.showNotFoundDialog(err)
      throw new Error(err)
    }

    if (userId) {
      this.linkedGroup.assigned = await this.props.user!.getAssignedGroups(userId)

      const assignedGroupIds = getIdList<GroupElementProps>(this.linkedGroup.assigned)
      this.linkedGroup.unassigned = groupList.filter(({ id }) => assignedGroupIds.indexOf(id!) === -1)
    } else {
      const [firstGroup, ...others] = groupList
      this.linkedGroup = {
        unassigned: others,
        assigned: [firstGroup],
      }
    }
  }

  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  _handleFormSubmit: ReactFormEvent = (e) => {
    e.preventDefault()
  }

  getFormData = () => {
    const linkedGroupRef = this._linkedGroupRef.current!
    const selectedElement = linkedGroupRef.getValue()

    if (!selectedElement || selectedElement.length === 0) {
      // prettier-ignore
      const eMessage = Intl.getIntlText({ id: 'htmljs_react.mgmt.userassignedgroupcontainer.dialog_desc_least_group_required' })
      linkedGroupRef.setError(eMessage)
      Logger.errLog(eMessage)
      return null
    }

    return selectedElement.map(({ value }: MultiSelectItemProps) => value)
  }
}

export default UserAssignedGroupContainer
